import { useEffect, useRef } from 'react';
import { IconChevronDown, IconChevronUp, IconLoader2, IconSearch, IconX } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';

/** Find-in-document bar. Matching happens in the editor shell. */
export default function SearchBar({ query, onQuery, hits, index, running, onStep, onClose }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    const total = hits.length;

    return (
        <div className="flex h-12 flex-none items-center gap-2 border-b border-[#ECE8E4] bg-white px-3 dark:border-zinc-800 dark:bg-zinc-900">
            <IconSearch size={16} stroke={1.8} className="text-[#A8A099]" />
            <input
                ref={inputRef}
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onStep(e.shiftKey ? -1 : 1);
                    if (e.key === 'Escape') onClose();
                    e.stopPropagation();
                }}
                placeholder={t('pdfEditor.searchPlaceholder')}
                className="h-8 w-[300px] rounded-lg border border-[#ECE8E4] bg-white px-2 text-[13px] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
            />

            <span className="min-w-[92px] text-[12px] text-[#6B635C] dark:text-zinc-400">
                {running ? (
                    <IconLoader2 size={14} className="animate-spin" />
                ) : total ? (
                    t('pdfEditor.searchCount', {
                        index: toBnDigits(index + 1),
                        total: toBnDigits(total),
                    })
                ) : query ? t('pdfEditor.searchNone') : ''}
            </span>

            <button
                type="button"
                onClick={() => onStep(-1)}
                disabled={!total}
                aria-label={t('pdfEditor.searchPrev')}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECE8E4] text-[#3D3833] disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-200"
            >
                <IconChevronUp size={16} stroke={1.9} />
            </button>
            <button
                type="button"
                onClick={() => onStep(1)}
                disabled={!total}
                aria-label={t('pdfEditor.searchNext')}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECE8E4] text-[#3D3833] disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-200"
            >
                <IconChevronDown size={16} stroke={1.9} />
            </button>

            <button
                type="button"
                onClick={onClose}
                aria-label={t('common.close')}
                className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-[#6B635C] hover:bg-[#F4F1ED] dark:text-zinc-400 dark:hover:bg-zinc-800"
            >
                <IconX size={16} stroke={1.8} />
            </button>
        </div>
    );
}
